import { Classification } from '@/store/classificationsApi';
import {
  ClassificationIdNumber,
  ClassificationIds,
  SubClassificationIds,
} from '@/utils/enums';
import { includesSearchText } from '@/utils/string';

/**
 * Ensures `subClassificationId` is a **direct child** of `classificationId` in the
 * classification tree returned by `GET .../classifications`.
 *
 * Offline / legacy API data sometimes stores the **parent** id as `SubClassificationId`
 * (e.g. Real Estate `ClassificationId === ClassificationIds.REAL_ESTATE` with a duplicate
 * parent id in `SubClassificationId`), or uses
 * subtype ids that no longer exist in the current tree. The UI and PUT payload must use a
 * valid child id so MUI selects and backend classification updates stay consistent.
 */
export function normalizeSubClassificationId(
  classifications: Classification[],
  classificationId: number,
  subClassificationId: number,
): number {
  const children = classifications.filter(
    (c) => c.ParentId === classificationId,
  );
  if (children.length === 0) {
    return subClassificationId;
  }
  if (children.some((c) => c.ClassificationId === subClassificationId)) {
    return subClassificationId;
  }
  return children[0].ClassificationId;
}

export interface ClassificationItem extends Omit<
  Classification,
  'IsAsset' | 'Sort'
> {
  isAsset: boolean;
  isLiability: boolean;
  /**
   * UI identity for selections/keys. Some shortcuts (e.g. Vehicle) intentionally share the same
   * level-1 `ClassificationId` as their parent category but must still be unique in the UI.
   */
  uiKey?: string;
  /**
   * Optional sub-classification that should be preselected for this category.
   * Used for UX shortcuts like a "Vehicle" card that actually creates a Personal Property account
   * with Vehicle as the default subtype.
   */
  presetSubClassificationId?: SubClassificationIds;
}

export interface ClassificationGroup {
  name: string;
  parentId: number;
  items: ClassificationItem[];
}

const CategoryUtility = () => {
  const getClassificationByClassificationId = ({
    classifications,
    classificationId,
  }: {
    classifications: Classification[];
    classificationId: Classification['ClassificationId'];
  }): ClassificationItem | undefined => {
    const classification = classifications.find(
      (x) => x.ClassificationId === classificationId,
    );

    if (!classification) {
      return undefined;
    }

    const classificationItem: ClassificationItem = {
      ...classification,
      isLiability: false,
      isAsset: false,
    };

    if (isAsset(classification, classifications)) {
      classificationItem.isAsset = true;
    }

    if (isLiability(classification, classifications)) {
      classificationItem.isLiability = true;
    }

    return classificationItem;
  };

  const isAsset = (
    classification: Classification,
    classifications: Classification[],
  ) => {
    if (classification.ParentId !== ClassificationIdNumber.ASSET) {
      const parentCategory = classifications.find(
        (x) => x.ClassificationId === classification.ParentId,
      );

      return (
        parentCategory !== undefined &&
        parentCategory.ParentId === ClassificationIdNumber.ASSET
      );
    }

    return true;
  };

  const isLiability = (
    classification: Classification,
    classifications: Classification[],
  ) => {
    if (classification.ParentId !== ClassificationIdNumber.LIABILITY) {
      const parentCategory = classifications.find(
        (x) => x.ClassificationId === classification.ParentId,
      );

      return (
        parentCategory !== undefined &&
        parentCategory.ParentId === ClassificationIdNumber.LIABILITY
      );
    }

    return true;
  };

  const isRealEstate = (
    classificationId: Classification['ClassificationId'],
  ) => {
    if (!classificationId) {
      return false;
    }

    return classificationId === ClassificationIdNumber.REAL_ESTATE;
  };

  /**
   * Alphabetize items within the ClassificationGroup.
   * This ensures that the list will always be displayed in the same
   * order.
   */
  const alphabetize = (
    classificationGroups: ClassificationGroup[],
  ): ClassificationGroup[] => {
    return classificationGroups.map((classificationGroup) => ({
      ...classificationGroup,
      items: [...classificationGroup.items].sort((a, b) =>
        a.Name.localeCompare(b.Name),
      ),
    }));
  };

  /**
   * Returns an alphabetized group of Assets and Liabilities.
   */
  const categorize = ({
    classifications,
    label,
  }: {
    classifications: Classification[];
    label: { asset: string; liability: string };
  }): ClassificationGroup[] => {
    const initialize: ClassificationGroup[] = [
      { name: label.asset, items: [], parentId: ClassificationIds.ASSET },
      {
        name: label.liability,
        items: [],
        parentId: ClassificationIds.LIABILITY,
      },
    ];

    const classificationGroups = classifications.reduce(
      (accumulator: ClassificationGroup[], classification) => {
        // Only include main classifications (direct children of Asset/Liability).
        // Sub-classifications are selected later on the details form.
        if (
          classification.ParentId !== ClassificationIdNumber.ASSET &&
          classification.ParentId !== ClassificationIdNumber.LIABILITY
        ) {
          return accumulator;
        }

        const updated = {
          ...classification,
          isAsset: false,
          isLiability: false,
        };

        if (isAsset(classification, classifications)) {
          updated.isAsset = true;
          accumulator[0].items.push(updated);
        }

        if (isLiability(classification, classifications)) {
          updated.isLiability = true;
          accumulator[1].items.push(updated);
        }

        return accumulator;
      },
      initialize,
    );

    return alphabetize(classificationGroups);
  };

  const getSubcategories = (
    classification: ClassificationItem,
    classifications: Classification[],
  ): Classification[] => {
    const result = classifications.filter((x) => {
      return x.ParentId === classification.ClassificationId;
    });

    return result;
  };

  const searchCategoryByName = ({
    searchTerm,
    classifications,
  }: {
    searchTerm: string;
    classifications: ClassificationGroup[];
  }): ClassificationItem[] => {
    return classifications
      .flatMap((x) => x.items)
      .filter((x) => includesSearchText(x.Name, searchTerm));
  };

  return {
    categorize,
    searchCategoryByName,
    getClassificationByClassificationId,
    getSubcategories,
    isRealEstate,
  };
};

export default CategoryUtility();
